import { useState } from "react";
import { useBackState } from "@/hooks/useScrollMemory";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger } from "@/components/ui/sheet";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TruckMergeAction } from "@/components/TruckMergeDialog";
import { AlertTriangle, ShieldAlert, CheckCircle2, Info, TagsIcon, Copy, ScanLine, ChevronRight,
         MapPinOff, MapPin, FileX2, FileClock, ImageOff, ListX, FileWarning, ClipboardList, HelpCircle } from "lucide-react";

// INVENTORY HEALTH — one band, and every number on it is a door.
//
// These counts used to be scattered: some inside the text of seven dropdown options, some behind
// an attention drawer, some nowhere at all. A number you have to open a dropdown to read is not a
// dashboard. So: one tile per condition, the count on its face, and CLICKING IT applies exactly the
// filter that returns those rows — the tile's number and the list underneath can never disagree,
// because the server computes both from the same predicate (see _shared.SPEC_COUNT_EXPR and
// no_location_query).
//
// ONE FACT, ONE PLACE: a count lives on a tile OR in a dropdown label, never both.
//
// AND EVERY NUMBER SAYS HOW MUCH OF ITSELF IS SEEDED — "42 (30 SYNTHETIC)", never a bare "42" and
// never just the word SYNTHETIC, which left you unable to tell 2 real trucks from 40 (owner ruling
// 2026-09-23). The split comes from the server, through the same predicate as the count it splits.
const TONE = {
  red:   "border-red-200 bg-red-50/70 hover:bg-red-50 text-[#B01E28]",
  amber: "border-amber-200 bg-amber-50/70 hover:bg-amber-50 text-amber-800",
  slate: "border-slate-200 bg-white hover:bg-slate-50 text-slate-600",
  green: "border-emerald-200 bg-emerald-50/70 hover:bg-emerald-50 text-emerald-800",
};

function Synth({ testid, n = 0, show }) {
  // WHILE THE SEED IS UP, EVERY COUNT CARRIES ITS SHARE — including "(0 SYNTHETIC)". Showing the
  // marker only when the share is non-zero left "64" bare, and a bare number is the one that gets
  // taken as real by accident (owner, 2026-09-23). Absence of a marker must never need decoding.
  // In production nothing is seeded, `show` is false, and none of this renders.
  if (!show) return null;
  return (
    <span data-testid={testid} className={`text-[9px] font-mono font-bold leading-none whitespace-nowrap ${n > 0 ? "text-[#B01E28]" : "text-slate-400"}`}>
      ({n} SYNTHETIC)
    </span>
  );
}

function Tile({ testid, icon, label, count, sub, tone = "slate", active, synthetic = 0, seedUp, onClick, always }) {
  const quiet = !count && !always;
  return (
    <button type="button" data-testid={testid} onClick={onClick} disabled={(quiet && !active) || !onClick}
      className={`group rounded-xl border px-3 py-2.5 text-left transition-all
        ${quiet ? "border-slate-200 bg-white text-slate-400" : TONE[tone]}
        ${quiet && !active ? "cursor-default" : "hover:-translate-y-px cursor-pointer"}
        ${active ? "ring-2 ring-[#E0781A]/60 border-[#E0781A]/40" : ""}`}>
      <span className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider opacity-70">
        {icon}{label}
      </span>
      <span className="mt-1 flex flex-wrap items-baseline gap-x-1">
        <b data-testid={`${testid}-count`} className="font-display text-xl leading-none tabular-nums">{count}</b>
        {count > 0 && <Synth testid={`${testid}-synth`} n={synthetic} show={seedUp} />}
      </span>
      {sub && <span className="mt-0.5 block text-[10px] leading-tight text-slate-400">{sub}</span>}
    </button>
  );
}

export function TruckHealthBand({ kpis, params = {}, dupItems = [], vinAudit = { count: 0, items: [] },
                                  nearVin = [], nearVinTotal = null, asks = { open: 0, items: [] }, serialDups = { groups: [] },
                                  onMerged, canPrintTags, onJump, onCategory, onReview }) {
  const [open, setOpen] = useBackState("thb.open", false);
  const [brk, setBreak] = useBackState("thb.brk", false);
  // COLLAPSED TO ONE LINE BY DEFAULT, and it remembers PER PERSON (owner ruling 2026-09-24).
  // Seven tiles always open is what made this page read as a control panel, and that doesn't stop
  // being true just because only the owner is looking at it. The key carries the account, so one
  // person's choice on a shared yard tablet is never the next person's.
  const { user } = useAuth();
  const navigate = useNavigate();
  const memKey = `fat_health_band_open:${user?.email || "anon"}`;
  const [show, setShow] = useState(() => localStorage.getItem(memKey) === "1");
  const toggle = () => { const v = !show; setShow(v); localStorage.setItem(memKey, v ? "1" : "0"); };
  if (!kpis) return <div data-testid="health-band-loading" className="h-20 rounded-xl bg-slate-100 animate-pulse mb-4" />;

  const ch = kpis.capture_health || {};
  const chs = ch.synthetic || {};
  const perCat = ch.per_category || [];
  const cap = kpis.capture || {};
  const caps = kpis.capture_synthetic || {};
  const a = kpis.alerts || {};
  const as = a.synthetic || {};
  const syn = kpis.synthetic?.active ? kpis.synthetic.synthetic : 0;
  // WHAT'S MISSING'S OWN COUNT (completeness.truck_counts) — where this band and that list count the same thing,
  // the band shows that number, so the two can never disagree (his rule, 28 Oct).
  const wm = ch.complete || { total: 0, complete: 0, gaps: {}, marked: {} };
  const wmSyn = wm.synthetic || { gaps: {} };
  const noPhotos = wm.gaps.photos || 0;
  const sheetMarked = wm.marked?.build_sheet || 0;
  const prefix = kpis.synthetic?.prefix || "SEED_";
  const needsInfo = a.needs_info || 0;
  // The real number, from the same aggregation the tag filter narrows on. This read used to be
  // `kpis.no_tag`, a field the KPI endpoint has never returned — so "No tag printed" silently
  // never appeared in this drawer at all. A row that can only ever be invisible is not a row.
  const noTag = cap.tag_print?.unprinted || 0;
  const dupVin = a.vin_duplicates || 0;
  const invalidVin = vinAudit.count || 0;
  const dupSerial = a.serial_duplicates || 0;
  const needsReview = a.needs_review || 0;
  // NEAR-VIN LOOK-ALIKES — one character apart, almost always a typo. Only ever populated for an
  // account holding `merge_trucks`: "if someone doesn't have the merge permission, they must not
  // know it exists — no near-VIN flags. Absent, not blocked." (owner ruling 2026-09-28)
  const nearCount = nearVinTotal ?? nearVin.length;
  // SOMEBODY IS ASKING about a component nobody has looked at. Counted with the flags because it
  // is work on a vehicle, and it is the one flag a CUSTOMER is waiting on.
  const askCount = asks?.open || 0;
  const attentionTotal = needsInfo + dupVin + invalidVin + dupSerial + needsReview + nearCount + askCount + (canPrintTags ? noTag : 0);
  // The bad-VIN flags come from the audit endpoint, which hands over the rows themselves — so this
  // share is counted off those rows rather than guessed, and the attention total's SYNTHETIC figure
  // covers every flag inside it instead of most of them.
  const invalidVinSyn = (vinAudit.items || []).filter((t) => (t.stock_number || "").startsWith(prefix)).length;
  const noTagSyn = caps.tag_print?.unprinted || 0;
  const attentionSyn = (as.needs_info || 0) + (as.vin_duplicates || 0) + invalidVinSyn
    + (as.serial_duplicates || 0) + (as.needs_review || 0) + (canPrintTags ? noTagSyn : 0);
  const hasBlocker = dupVin > 0 || dupSerial > 0 || invalidVin > 0;
  const is = (k, v) => (params[k] || "") === v;

  // The fraction with the seeded trucks taken out of BOTH halves — the only way to read what the
  // real fleet's capture depth is while a synthetic fleet is up.
  const realRec = (ch.recorded || 0) - (chs.recorded || 0);
  const realDef = (ch.defined || 0) - (chs.defined || 0);

  // THE CLOSED LINE CARRIES THE ONE NUMBER THAT MOVES. A tile count that sits at its maximum
  // forever ("no location: 241 of 241") teaches you to stop reading it; the capture fraction goes
  // up as the crew works, which is the only reason to glance at a collapsed line at all.
  if (!show) {
    return (
      <div data-testid="truck-health-band" className="mb-4">
        <button type="button" data-testid="health-band-expand" onClick={toggle}
          className="group flex w-full flex-wrap items-baseline gap-x-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-left transition-colors hover:bg-slate-50">
          <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400">Inventory health</span>
          {ch.pct == null
            ? <span className="text-xs text-slate-500">no spec fields defined yet — nothing to measure against</span>
            : <span data-testid="health-band-closed-fraction" className="text-xs text-slate-600">
                <b className="text-slate-900">{(ch.recorded || 0).toLocaleString()}</b> of{" "}
                <b className="text-slate-900">{(ch.defined || 0).toLocaleString()}</b> specs recorded
                <span className="text-slate-400"> · {ch.pct}%</span>
              </span>}
          <span data-testid="health-band-closed-complete" className="text-xs text-slate-600">
            · <b className="text-emerald-700">{wm.complete.toLocaleString()}</b> of {wm.total.toLocaleString()} trucks complete
            {syn > 0 && <> <Synth testid="health-band-closed-complete-synth" n={wmSyn.complete} show /></>}
          </span>
          {syn > 0 && realDef > 0 && (
            <span data-testid="health-band-closed-real" className="text-[11px] font-bold text-[#B01E28]">
              · real fleet only: {realRec.toLocaleString()} of {realDef.toLocaleString()}
            </span>
          )}
          {attentionTotal > 0 && (
            <span data-testid="health-band-closed-attention" className="text-[11px] text-[#C76714]">· {attentionTotal} flag{attentionTotal === 1 ? "" : "s"} need attention</span>
          )}
          <span className="ml-auto flex items-center gap-1 text-[11px] font-semibold text-[#C76714] group-hover:underline">
            Show<ChevronRight className="w-3.5 h-3.5" />
          </span>
        </button>
      </div>
    );
  }

  return (
    <div data-testid="truck-health-band" className="mb-4">
      {/* HEADLINE: capture depth, with the denominator said out loud — never a bare percentage.
          "4 of 6" on a trailer is nearly done; "4 of 53" would be alarming and wrong, and that
          difference only exists if the number states what it is measured against. */}
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 mb-2">
        <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400">Inventory health</span>
        <button type="button" data-testid="health-band-collapse" onClick={toggle}
          className="text-[11px] font-semibold text-[#C76714] hover:underline">hide</button>
        {ch.pct == null ? (
          <span data-testid="capture-depth-unmeasurable" className="text-xs text-slate-500">
            Capture depth <b className="text-slate-700">not measurable yet</b> — no spec fields are defined for
            these vehicles' categories, so there is nothing to measure against.
          </span>
        ) : (
          <span data-testid="capture-depth" className="text-xs text-slate-600">
            Capture depth <b data-testid="capture-depth-pct" className="text-slate-900">{ch.pct}%</b>
            <span className="text-slate-400"> — </span>
            <b className="text-slate-700">{(ch.recorded || 0).toLocaleString()}</b> of{" "}
            <b className="text-slate-700">{(ch.defined || 0).toLocaleString()}</b> specs recorded, each truck
            against <b>its own vehicle type's field list</b>
          </span>
        )}
        {/* A fraction cannot carry "(30 SYNTHETIC)" without being ambiguous about which half, so it
            states the REAL-ONLY fraction instead — same information, no guessing. */}
        {syn > 0 && realDef > 0 && (
          <span data-testid="capture-depth-real" className="text-[11px] font-bold text-[#B01E28]">
            · real fleet only: {realRec.toLocaleString()} of {realDef.toLocaleString()} ({Math.round(100 * realRec / realDef)}%)
          </span>
        )}
        {perCat.length > 0 && (
          <button type="button" data-testid="capture-depth-breakdown-toggle" onClick={() => setBreak(!brk)}
            className="text-[11px] font-semibold text-[#C76714] hover:underline">
            {brk ? "hide" : "per vehicle type"}
          </button>
        )}
        {ch.unmeasurable_trucks > 0 && (
          <span data-testid="capture-depth-unmeasured-note" className="text-[11px] text-[#C76714]">
            · {ch.unmeasurable_trucks} not measurable <Synth testid="capture-depth-unmeasured-synth" n={chs.unmeasurable_trucks} show={syn > 0} /> (no
            field set defined for their category — excluded, not scored 0)
          </span>
        )}
        {ch.off_set > 0 && (
          <Link to="/health" data-testid="capture-depth-offset" className="text-[11px] text-[#B01E28] underline hover:no-underline">
            · {ch.off_set} recorded value{ch.off_set === 1 ? "" : "s"} <Synth testid="capture-depth-offset-synth" n={chs.off_set} show={syn > 0} /> sit{ch.off_set === 1 ? "s" : ""} outside its
            vehicle type's field set — see which
          </Link>
        )}
        {syn > 0 && (
          <span data-testid="health-band-synthetic" className="text-[11px] font-bold text-[#B01E28]">
            · {syn} of the {kpis.synthetic?.active_total || 0} vehicles counted on this page are SYNTHETIC ({prefix}…)
          </span>
        )}
      </div>

      {brk && (
        <div data-testid="capture-depth-breakdown" className="mb-2 rounded-xl border border-slate-200 bg-slate-50/70 p-3">
          <table className="w-full text-[11px]">
            <tbody>
              {perCat.map((c) => {
                const cs = c.synthetic || {};
                const rr = (c.recorded || 0) - (cs.recorded || 0);
                const rd = (c.defined || 0) - (cs.defined || 0);
                return (
                  <tr key={c.code} data-testid={`capture-cat-${c.code}`} className="border-b border-slate-200/70 last:border-0">
                    <td className="py-1 pr-3 font-semibold text-slate-700">{c.name || c.code}</td>
                    <td className="py-1 pr-3 text-slate-500 tabular-nums whitespace-nowrap">
                      {c.trucks} truck{c.trucks === 1 ? "" : "s"} <Synth testid={`capture-cat-${c.code}-synth`} n={cs.trucks} show={syn > 0} />
                    </td>
                    <td className="py-1 pr-3 tabular-nums">
                      {c.measurable
                        ? <><b className="text-slate-800">{c.recorded.toLocaleString()}</b> of <b className="text-slate-800">{c.defined.toLocaleString()}</b>
                            <span className="text-slate-400"> · {c.defined_per_truck} fields defined per truck</span>
                            {cs.trucks > 0 && rd > 0 && (
                              <span data-testid={`capture-cat-${c.code}-real`} className="ml-1.5 font-bold text-[#B01E28]">
                                · real only: {rr.toLocaleString()} of {rd.toLocaleString()}
                              </span>
                            )}</>
                        : <span className="text-[#C76714]">no spec fields defined for this type — not measurable</span>}
                    </td>
                    <td className="py-1 text-right text-[#B01E28]">{c.off_set > 0 ? `${c.off_set} outside the set` : ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p data-testid="capture-depth-denominator-note" className="mt-2 text-[11px] leading-relaxed text-slate-500">
            The denominator is <b>your spec-field list</b>, read live. Add two fields to Heavy Truck this
            afternoon and every heavy truck's fraction drops the same afternoon — the denominator moved,
            the data didn't get worse. <Link to="/vehicle-setup" className="text-[#C76714] underline hover:no-underline">Vehicle Setup</Link> is
            where that list lives.
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-5 gap-2">
        <Tile testid="health-tile-complete" icon={<CheckCircle2 className="w-3 h-3" />} label="Complete"
          count={wm.complete} always tone="green" synthetic={wmSyn.complete || 0} seedUp={syn > 0}
          sub={`of ${wm.total.toLocaleString()} trucks · What's missing lists each one's gaps`}
          onClick={wm.open ? () => navigate("/catch-up#whats-missing") : undefined} />

        {/* THE SPLIT, ON TWO TILES. He asked for it directly: "how many are in a holding area like
            the fenced lot, and how many have genuinely no location at all? Those are very
            different problems." A holding area no longer counts as lost (it's a place you can walk
            to), so this tile counts ONLY the trucks with no location on the record, and the parked
            ones get their own number beside it — two numbers, one click each. The holding tile
            only appears when there IS something parked, because a zero with nothing behind it is
            just another tile in the way. */}
        <Tile testid="health-tile-no-location" icon={<MapPinOff className="w-3 h-3" />} label="No place"
          count={wm.gaps.placed || 0} sub="none on record, or its place was archived"
          tone="red" synthetic={wmSyn.gaps.placed || 0} seedUp={syn > 0}
          active={is("placement", "no_real_place")}
          onClick={() => onJump({ placement: is("placement", "no_real_place") ? "all" : "no_real_place" })} />

        {(ch.in_holding || 0) > 0 && (
          <Tile testid="health-tile-in-holding" icon={<MapPin className="w-3 h-3" />} label="In a holding area"
            count={ch.in_holding || 0} sub="parked, not put away" tone="amber"
            synthetic={chs.in_holding || 0} seedUp={syn > 0}
            active={is("placement", "holding")}
            onClick={() => onJump({ placement: is("placement", "holding") ? "all" : "holding" })} />
        )}

        <Tile testid="health-tile-no-specs" icon={<ListX className="w-3 h-3" />} label="No specs"
          count={ch.no_specs || 0} sub="nothing recorded at all" tone="amber" synthetic={chs.no_specs || 0} seedUp={syn > 0}
          active={is("capture", "none")}
          onClick={() => onJump({ capture: is("capture", "none") ? "" : "none", sheet: "" })} />

        {/* "Thin capture" was the owner's phrase from a conversation, not a word anyone in the yard
            uses, and it needed its own sub-line to be understood. Renamed on his ruling. */}
        <Tile testid="health-tile-thin-capture" icon={<FileWarning className="w-3 h-3" />} label="Barely recorded"
          count={ch.thin || 0} sub={`sheet on file, ${ch.thin_max ?? 8} specs or fewer off it`} tone="red"
          synthetic={chs.thin || 0} seedUp={syn > 0} active={is("capture", "thin")}
          onClick={() => onJump(is("capture", "thin") ? { capture: "", sheet: "" } : { capture: "thin", sheet: "has" })} />

        {/* "Read" is how the tool talks about itself; the button these trucks are waiting on says
            "Extract specs from sheet". So the tile says what is TRUE OF THE TRUCK instead. */}
        <Tile testid="health-tile-sheet-unread" icon={<FileClock className="w-3 h-3" />} label="Sheet waiting"
          count={cap.sheet?.attached || 0} sub="on file, specs not pulled off it yet" tone="amber"
          synthetic={caps.sheet?.attached || 0} seedUp={syn > 0} active={is("sheet", "attached")}
          onClick={() => onJump({ sheet: is("sheet", "attached") ? "" : "attached", capture: "" })} />

        <Tile testid="health-tile-no-sheet" icon={<FileX2 className="w-3 h-3" />} label="No build sheet"
          count={wm.gaps.build_sheet || 0} synthetic={wmSyn.gaps.build_sheet || 0} seedUp={syn > 0}
          sub={sheetMarked > 0 ? `no sheet on file · ${sheetMarked} more marked "none available", not a gap` : "no sheet on file to work from"}
          active={is("sheet", "missing")}
          onClick={() => onJump({ sheet: is("sheet", "missing") ? "" : "missing", capture: "" })} />

        {/* HIS RULING: this tile is the honest "what still needs reading" list. It counts every
            truck holding a photo nobody has read — all of them, or the three shot this morning —
            and the rows say how many are left on each. It used to count only trucks where
            NOTHING had ever been read, so a truck stamped "read" after 6 of its 61 photos went
            to the AI was invisible on it. Trucks with no photos at all are a different job and
            are reported on the sub-line rather than folded into the count. */}
        <Tile testid="health-tile-no-photos" icon={<ImageOff className="w-3 h-3" />} label="No photo read"
          count={cap.photos?.unread || 0} synthetic={caps.photos?.unread || 0} seedUp={syn > 0}
          sub={noPhotos > 0
            ? `photos waiting to be read · ${noPhotos.toLocaleString()} more truck${noPhotos === 1 ? "" : "s"} have no photos (What's missing's count)`
            : "photos waiting to be read · not part of \"complete\""}
          active={is("photos", "unread")}
          onClick={() => onJump({ photos: is("photos", "unread") ? "" : "unread" })} />

        {/* HIS NUMBER, IN HIS WORDS: "a decoded build sheet with no presence check is a catalogue
            of parts I don't have." This counts exactly those trucks, and clicking it goes straight
            INTO the review on the first one rather than handing him a list to work through.
            NOT REVIEWED is his own word for the activity ("I review and confirm"), and it is only
            safe now that the vehicle-type badge says "Type not confirmed" instead of "Needs
            review" — two different things called review on one screen was the collision. */}
        <Tile testid="health-tile-presence" icon={<ClipboardList className="w-3 h-3" />} label="Not reviewed"
          count={ch.presence_unchecked || 0} tone="red"
          sub="sheet read · nobody's said what's still on it"
          synthetic={chs.presence_unchecked || 0} seedUp={syn > 0}
          onClick={() => onReview?.()} />

        {/* Duplicates, bad VINs, needs-info and no-tag stay in ONE tile that opens the drawer that
            already lists them — six separate tiles for those is how six tiles becomes twelve. */}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            {attentionTotal > 0 ? (
              <button type="button" data-testid="cmd-attention-open"
                className={`rounded-xl border px-3 py-2.5 text-left transition-all hover:-translate-y-px ${hasBlocker ? "border-red-300 bg-red-50 text-[#B01E28]" : "border-amber-300 bg-amber-50 text-amber-800"}`}>
                <span className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider opacity-70">
                  {hasBlocker ? <ShieldAlert className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}Needs attention
                </span>
                <span className="mt-1 flex flex-wrap items-baseline gap-x-1">
                  <b data-testid="health-tile-attention-count" className="font-display text-xl leading-none tabular-nums">{attentionTotal}</b>
                  <Synth testid="health-tile-attention-synth" n={attentionSyn} show={syn > 0} />
                  <ChevronRight className="w-3.5 h-3.5 opacity-60" />
                </span>
                <span className="mt-0.5 block text-[10px] leading-tight text-slate-400">flags, not vehicles — open for the list</span>
              </button>
            ) : (
              <button type="button" data-testid="cmd-attention-clear"
                className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-left text-slate-500">
                <span className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider opacity-70">
                  <CheckCircle2 className="w-3 h-3 text-emerald-500" />Needs attention
                </span>
                <span className="mt-1 block font-display text-xl leading-none text-slate-400">0</span>
                <span className="mt-0.5 block text-[10px] text-slate-400">all clear</span>
              </button>
            )}
          </SheetTrigger>
          <SheetContent side="right" data-testid="attention-drawer" className="w-[92vw] sm:max-w-md overflow-y-auto p-5">
            <SheetHeader><SheetTitle className="font-display">Needs attention</SheetTitle>
              <SheetDescription className="text-xs text-slate-500">Vehicles and equipment flagged for review, missing tags, or possible duplicates.</SheetDescription>
            </SheetHeader>
            {/* THE ARITHMETIC, STATED. 193 flags on a 128-truck fleet is not a broken number —
                one truck can be uncategorized AND untagged. Say so rather than let a total
                bigger than the fleet discredit itself. */}
            <p data-testid="attention-arithmetic" className="mt-3 font-mono text-[10px] text-slate-400">
              {attentionTotal} flag{attentionTotal === 1 ? "" : "s"} = {[
                needsInfo && `${needsInfo} needs info`,
                canPrintTags && noTag && `${noTag} no tag`,
                needsReview && `${needsReview} uncategorized`,
                dupVin && `${dupVin} duplicate VIN`,
                nearCount && `${nearCount} look-alike VIN`,
                askCount && `${askCount} being asked about`,
                invalidVin && `${invalidVin} bad VIN`,
                dupSerial && `${dupSerial} duplicate serial`,
              ].filter(Boolean).join(" + ")} · one vehicle can carry more than one{" "}
              {attentionSyn > 0 && (
                <b data-testid="attention-arithmetic-synth" className="ml-1 text-[#B01E28]">
                  · {attentionSyn} of these flags are raised by SYNTHETIC trucks
                </b>
              )}
            </p>
            <div className="mt-4 space-y-2">
              {/* A CUSTOMER IS WAITING ON THIS ONE. Every other flag here is our own tidiness;
                  this is somebody at the counter who can't answer a question yet. It sits first
                  for that reason, and it names the trucks so the walk can be planned. */}
              {askCount > 0 && (
                <div data-testid="attn-assess-asks" className="rounded-lg border border-[#E0781A]/50 bg-[#FFF7ED] px-3 py-2">
                  <div className="flex items-center gap-2">
                    <HelpCircle className="w-4 h-4 text-[#C76714]" />
                    <b className="font-display text-sm text-[#8a4b0b]">Components somebody is asking about</b>
                    <span data-testid="attn-assess-asks-count" className="ml-auto font-mono text-base font-bold tabular-nums text-[#C76714]">{askCount}</span>
                  </div>
                  <div className="mt-1.5 space-y-1">
                    {(asks.items || []).slice(0, 4).map((a) => (
                      <Link key={a.id} data-testid={`attn-ask-${a.stock_number}-${a.component}`}
                        to={`/donors/${a.donor_id}`} onClick={() => setOpen(false)}
                        className="block text-[11px] text-slate-600 hover:text-[#B01E28] hover:underline">
                        <span className="font-mono">{a.stock_number}</span> — {a.component_label}
                        {a.count > 1 ? <b className="text-[#C76714]"> · asked {a.count} times</b> : null}
                        {a.needs_more_than_a_look ? <span className="text-slate-400"> · somebody went and still couldn’t tell</span> : null}
                      </Link>
                    ))}
                  </div>
                  <Link data-testid="attn-assess-asks-all" to="/requests?type=assess" onClick={() => setOpen(false)}
                    className="mt-1.5 inline-block text-[11px] font-semibold text-[#C76714] hover:underline">
                    {askCount > 4 ? `all ${askCount} in the requests queue` : "open the requests queue"} →
                  </Link>
                </div>
              )}
              <AttnRow testid="attn-needs-info" icon={<Info className="w-4 h-4 text-amber-600" />} label="Needs info" count={needsInfo}
                synthetic={as.needs_info || 0} seedUp={syn > 0}
                onClick={() => { onJump({ needs_info: "1", status: "all", category: "all" }); setOpen(false); }} />
              {canPrintTags && <AttnRow testid="attn-no-tag" icon={<TagsIcon className="w-4 h-4 text-slate-600" />} label="No tag printed" count={noTag}
                synthetic={noTagSyn} seedUp={syn > 0}
                onClick={() => { onJump({ tag_print: "unprinted", status: "all" }); setOpen(false); }} />}
              {/* The count ALSO lives in the category dropdown, and that is deliberate (owner ruling
                  2026-09-23): the dropdown answers "how many will I get if I filter to this", this
                  row answers "what needs fixing". Same query behind both — alerts.needs_review and
                  categories[].count are the same fold of the same $group — so they cannot disagree. */}
              <AttnRow testid="attn-needs-review" icon={<Info className="w-4 h-4 text-indigo-600" />} label="Uncategorized" count={needsReview}
                synthetic={as.needs_review || 0} seedUp={syn > 0}
                onClick={() => { onCategory("uncategorized"); setOpen(false); }} />
              <AttnGroup testid="attn-dup-vin" icon={<Copy className="w-4 h-4 text-[#B01E28]" />} label="Duplicate VINs" count={dupVin}
                synthetic={as.vin_duplicates || 0} seedUp={syn > 0}
                items={dupItems.map((g) => ({ label: g.vin || g._id || "—", trucks: g.trucks || g.donors || [] }))}
                renderAction={(it) => (it.trucks || []).slice(1).map((t) => (
                  <TruckMergeAction key={t.id} aId={it.trucks[0].id} bId={t.id} onDone={onMerged}
                    testid={`merge-dup-${t.stock_number}`}
                    trigger={<button data-testid={`merge-dup-${t.stock_number}-trigger`}
                      className="ml-1 text-[11px] font-bold text-[#B01E28] underline">
                      merge {it.trucks[0].stock_number} + {t.stock_number}</button>} />
                ))} />
              {/* ONE CHARACTER APART — flagged, never merged automatically, and dismissible for
                  good: "if two near-VIN trucks really are different, I say so once and it stops
                  showing." Renders only for an account that holds merge_trucks. */}
              <AttnGroup testid="attn-near-vin" icon={<Copy className="w-4 h-4 text-amber-600" />}
                label="Look-alike VINs (one character apart)" count={nearCount} seedUp={syn > 0}
                items={nearVin.map((p) => ({ label: `${p.a.vin_tail} vs ${p.b.vin_tail}`,
                                             trucks: [p.a, p.b], pair: p }))}
                renderAction={(it) => (
                  <NearVinActions pair={it.pair} onDone={onMerged} />
                )} />
              <AttnGroup testid="attn-invalid-vin" icon={<ScanLine className="w-4 h-4 text-[#B01E28]" />} label="Invalid / bad VIN" count={invalidVin}
                synthetic={invalidVinSyn} seedUp={syn > 0}
                items={(vinAudit.items || []).map((t) => ({ label: t.stock_number, trucks: [{ id: t.id, stock_number: t.stock_number }] }))} />
              <AttnGroup testid="attn-dup-serial" icon={<Copy className="w-4 h-4 text-[#B01E28]" />}
                label="Duplicate serials (across trucks)" count={dupSerial}
                synthetic={as.serial_duplicates || 0} seedUp={syn > 0}
                note={"The same serial recorded on two trucks means one of them is wrong — or the component was moved and the old truck still claims it. Open each truck and fix the one that's wrong, before the part is sold twice."
                  + (serialDups.capped ? " Showing the first 200 groups." : "")}
                items={(serialDups.groups || []).map((g) => ({
                  label: `${g.field_label}: ${g.value}`, trucks: g.trucks || [] }))} />
              {attentionTotal === 0 && <p className="text-sm text-slate-500 text-center py-8">Nothing needs attention. Nice.</p>}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
}

function AttnRow({ testid, icon, label, count, synthetic = 0, seedUp, onClick }) {
  if (!count) return null;
  return (
    <button data-testid={testid} onClick={onClick} className="w-full flex items-center gap-2.5 rounded-lg border border-slate-200 px-3 py-2.5 text-left hover:bg-slate-50 transition-colors">
      {icon}<span className="text-sm text-slate-700 flex-1">{label}</span>
      <Synth testid={`${testid}-synth`} n={synthetic} show={seedUp} />
      <span className="text-sm font-bold text-slate-900">{count}</span><ChevronRight className="w-4 h-4 text-slate-400" />
    </button>
  );
}

function AttnGroup({ testid, icon, label, count, synthetic = 0, seedUp, items, renderAction, note }) {
  const [exp, setExp] = useBackState(`thb.${testid}`, false);
  if (!count) return null;
  return (
    <div data-testid={testid} className="rounded-lg border border-red-200 bg-red-50/40">
      <button data-testid={`${testid}-toggle`} onClick={() => setExp(!exp)} className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left">
        {icon}<span className="text-sm text-slate-700 flex-1">{label}</span>
        <Synth testid={`${testid}-synth`} n={synthetic} show={seedUp} />
        <span className="text-sm font-bold text-[#B01E28]">{count}</span>
        <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${exp ? "rotate-90" : ""}`} />
      </button>
      {exp && (
        <div className="px-3 pb-2.5 space-y-1.5">
          {note && <p data-testid={`${testid}-note`} className="text-[11px] text-slate-500 leading-snug">{note}</p>}
          {count > Math.min(items.length, 25) && (
            <p data-testid={`${testid}-shown`} className="text-[11px] text-slate-500">Showing the first {Math.min(items.length, 25)} of {count}.</p>
          )}
          {items.slice(0, 25).map((it, i) => (
            <div key={i} data-testid={`${testid}-item-${i}`} className="text-xs bg-white rounded-md border border-slate-200 px-2 py-1.5">
              <span className="font-mono font-semibold text-slate-700">{it.label}</span>
              <span className="ml-2 text-slate-500">{(it.trucks || []).map((t) => (
                <Link key={t.id} to={`/donors/${t.id}`} className="underline hover:no-underline mr-1.5">{t.stock_number}</Link>
              ))}</span>
              {/* THE OPENER. Before this the trail ended at two clickable stock numbers — the merge
                  endpoint existed and nothing in the app called it. */}
              {renderAction && <span className="ml-1">{renderAction(it)}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NearVinActions({ pair, onDone }) {
  const [gone, setGone] = useState(false);
  if (gone) return <span className="ml-1 text-[11px] text-slate-400">not a duplicate — won't be flagged again</span>;
  return (
    <>
      <TruckMergeAction aId={pair.a.id} bId={pair.b.id} onDone={onDone}
        testid={`merge-near-${pair.a.stock_number}`}
        trigger={<button data-testid={`merge-near-${pair.a.stock_number}-trigger`}
          className="ml-1 text-[11px] font-bold text-[#B01E28] underline">look at these two</button>} />
      <button data-testid={`dismiss-near-${pair.a.stock_number}`}
        onClick={async () => {
          try {
            const { data } = await api.post("/truck-merge/dismiss", { a_id: pair.a.id, b_id: pair.b.id });
            setGone(true); if (data.message) toast.success(data.message);
            // ANSWER ON THE SPOT, THEN SETTLE. Refreshing immediately unmounts this row (the pair
            // is dismissed, so the count drops to zero) and his sentence — "I say so once and it
            // stops showing" — would flash past unread. The parent refresh follows a beat later so
            // the reply is readable AND no count is left disagreeing with the truth.
            setTimeout(() => onDone?.(), 2500);
          } catch { toast.error("Couldn't save that"); }
        }}
        className="ml-2 text-[11px] font-semibold text-slate-500 underline">not a duplicate</button>
    </>
  );
}
